import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { coverageQueries } from '@/lib/queries/coverage';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { resourceQueries } from '@/lib/queries/resource';

import { Resource } from './-components/Resource';

export const Route = createFileRoute('/_root/resource/$resourceId')({
  component: RouteComponent,
  validateSearch: searchParams =>
    z
      .object({
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .parse(searchParams),
  loaderDeps: ({ search: { from, to } }) => ({ from, to }),
  beforeLoad: ({ context: { formatMessage } }) => ({
    title: formatMessage({ id: 'global.breadcrumb.resource' }),
  }),
  loader: async ({
    context: { queryClient, toast },
    params: { resourceId },
    deps: { from, to },
  }) => {
    const defaultProject = await preLoadDefaultProject({
      context: { queryClient },
    });

    const resource = await queryClient.ensureQueryData(
      resourceQueries.detail(defaultProject.id, resourceId),
    );

    if (resource === null) {
      toast({
        title: 'global.error.resourceNotFound',
        variant: 'destructive',
      });
    }

    if (
      resource?.resource.type === 'CLOUD_RESOURCE' &&
      resource.resource.scannedInCloud
    ) {
      queryClient.prefetchQuery(coverageQueries.list(defaultProject.id));
      queryClient.prefetchQuery(
        resourceQueries.impacts(defaultProject.id, resourceId, from, to),
      );
    }

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const { resourceId } = Route.useParams();
  const { from, to } = Route.useSearch();

  const { data } = useSuspenseQuery(
    resourceQueries.detail(defaultProject.id, resourceId),
  );

  if (data === null) {
    throw new Error('Resource not found');
  }

  return (
    <Resource
      resource={data.resource}
      mainProject={defaultProject}
      resourceNodeId={resourceId}
      from={from}
      to={to}
    />
  );
}
